import React, { useEffect, useRef } from 'react';
import './cart-other-details.scss';
import { Switcher } from '../../../components';
import { TouchableOpacity } from '../../../common';
import { TextFieldComp } from './text-field-comp';
import { useCartOtherDetails } from '../../hooks/use-cart-other-details';
import { PoOrderShipping } from '@zonofi/zono-money-store';
import { AmountInputCard } from './amount-input-card';
import { useSearchParams } from 'react-router-dom';

interface CartOtherDetailsProps {
  onLocationClick: () => void;
  location?: PoOrderShipping;
  setOrderDetailsInfo?: React.Dispatch<
    React.SetStateAction<
      | {
          inputValues: Record<string, string>;
          includePaymentLink: boolean;
        }
      | undefined
    >
  >;
  isEditable?: boolean;
}

export const CartOtherDetails: React.FC<CartOtherDetailsProps> = ({
  onLocationClick,
  location,
  setOrderDetailsInfo,
  isEditable = true,
}) => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const isModify = source === 'modify';

  const advancePaymentRef = useRef<HTMLDivElement>(null);

  const {
    otherDetailsCard,
    inputValues,
    handleChange,
    includePaymentLink,
    togglePaymentLink,
    handleTypeSelect,
    handleDropdownToggle,
    dropdownOpen,
    setAmountValue,
    setTermsAndConditions,
    setPaymentTermsAndConditions,
  } = useCartOtherDetails(location);

  useEffect(() => {
    setOrderDetailsInfo?.({
      inputValues,
      includePaymentLink,
    });
  }, [inputValues, includePaymentLink]);

  useEffect(() => {
    if (isModify && advancePaymentRef.current) {
      setTimeout(() => {
        advancePaymentRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isModify]);

  return (
    <>
      <div className="other-details-container">
        <div className="other-details-view">
          {otherDetailsCard.map((item) => {
            return (
              <div key={item.key} className="other-details-info">
                <span className="other-details-info--title font-semibold">
                  {item?.title}
                </span>
                <div className="other-details-sub-view">
                  {item?.data.map((subItem) => (
                    <TextFieldComp
                      key={subItem?.key}
                      item={subItem}
                      value={inputValues[subItem.key] ?? ''}
                      onChange={handleChange(subItem.key)}
                      onLocationClick={onLocationClick}
                      disabled={!isEditable}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          <div
            ref={advancePaymentRef}
            className="other-details-info"
            style={
              isModify
                ? { border: '2px solid #000000' }
                : { border: '1px solid #d9d9d9' }
            }
          >
            <span className="other-details-info--title font-semibold">
              {'Advance Payment'}
            </span>
            <div className="other-details-sub-view">
              <TouchableOpacity
                className="other-details-check-card"
                onPress={togglePaymentLink}
                disabled={!isEditable}
              >
                <div>
                  <span className="other-details-check-card-text font-semibold">
                    {'Require advance payment for this order'}
                  </span>
                  <div className="other-details-check-card-sub-text font-regular">
                    {
                      'Customers must complete the payment before the order is confirmed.'
                    }
                  </div>
                </div>
                <Switcher
                  checked={includePaymentLink}
                  onChange={togglePaymentLink}
                  switcherStyles={{
                    switchSize: { width: 32, height: 20 },
                    thumbSize: { width: 14, height: 14 },
                    checkedTransform: 'translateX(12px)',
                    noSelectBg: '#F0F0F0',
                    thumbColor: includePaymentLink ? '#fff' : '#8C8C8C',
                  }}
                />
              </TouchableOpacity>

              {includePaymentLink && (
                <AmountInputCard
                  amountType={
                    inputValues?.advancePaymentType as 'percentage' | 'amount'
                  }
                  amountValue={inputValues?.advancePayment}
                  onChange={(value) => setAmountValue(value)}
                  handleTypeSelect={handleTypeSelect}
                  dropdownOpen={dropdownOpen}
                  handleDropdownToggle={handleDropdownToggle}
                  paymentTermsAndConditions={
                    inputValues?.paymentTermsAndConditions
                  }
                  onPaymentTermsChange={setPaymentTermsAndConditions}
                  disabled={!isEditable}
                  advancePaymentAmount={Number(
                    inputValues?.advancePaymentAmount
                  )}
                />
              )}
            </div>
          </div>

          <div className="other-details-info">
            <span className="other-details-info--title font-semibold">
              {'General Terms & Conditions'}
            </span>
            <div className="other-details-sub-view">
              <textarea
                value={inputValues?.termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                className="check-amount-box-text-area font-regular"
                style={{ overflow: 'hidden', resize: 'none' }}
                placeholder="Enter payment terms and conditions for the order"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
